import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { UnfinishedLectureDb } from '../../../../api/types/home-data/home-data';
import dateUtils from '../../../../utils/date-format';
import { manualFinishLectureOperation } from './operations/manual-finish-lecture-operation';
import globalConfigStore from '../../../../stores/global-config-store';

interface ManualFinishLectureDialogProps {
  visible: boolean;
  lecture: UnfinishedLectureDb;
  onHide: () => void;
  onSuccess: () => void;
}

export const ManualFinishLectureDialog: React.FC<
  ManualFinishLectureDialogProps
> = ({ visible, lecture, onHide, onSuccess }) => {
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize endTime when dialog opens
  useEffect(() => {
    if (visible) {
      setEndTime(new Date(lecture.scheduledEndTime));

      setError(null);
    }
  }, [visible]);

  const validateEndTime = (selectedEndTime: Date): string | null => {
    if (!selectedEndTime) {
      return 'יש לבחור זמן סיום';
    }

    // Calculate 5 hours after scheduled end time
    const maxEndTime = new Date(lecture.scheduledEndTime);
    maxEndTime.setHours(maxEndTime.getHours() + 5);

    if (selectedEndTime > maxEndTime) {
      return 'זמן הסיום לא יכול להיות יותר מ-5 שעות אחרי זמן הסיום המתוכנן';
    }

    if (selectedEndTime <= new Date(lecture.sessionStartAt)) {
      return 'זמן הסיום חייב להיות אחרי זמן ההתחלה בפועל';
    }

    return null;
  };

  const handleSave = async () => {
    if (!endTime) {
      setError('יש לבחור זמן סיום');
      return;
    }

    const validationError = validateEndTime(endTime);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    const branchId = globalConfigStore.rootFilter.currentBranches?.id;
    if (!branchId) {
      setError('לא נמצא מזהה סניף');
      setLoading(false);
      return;
    }

    const success = await manualFinishLectureOperation(
      branchId,
      lecture.programId,
      lecture.courseId,
      lecture.lectureId,
      lecture.lectureSessionId,
      endTime,
    );

    setLoading(false);

    if (success) {
      onSuccess();
    } else {
      setError('אירעה שגיאה בעדכון ההרצאה');
    }
  };

  const footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-start',
        gap: '8px',
        direction: 'rtl',
      }}
    >
      <Button
        label="שמור"
        icon="pi pi-check"
        onClick={handleSave}
        loading={loading}
        disabled={!endTime}
      />
      <Button
        label="ביטול"
        icon="pi pi-times"
        onClick={onHide}
        severity="secondary"
        outlined
        disabled={loading}
      />
    </div>
  );

  return (
    <Dialog
      header="סיום ידני של הרצאה"
      visible={visible}
      onHide={onHide}
      style={{
        width: '600px',
        backgroundColor: 'white',
        borderRadius: '8px',
      }}
      footer={footer}
    >
      <div style={{ direction: 'rtl' }}>
        {/* Lecture Information */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '12px' }}>
            פרטי ההרצאה:
          </div>
          <div style={{ marginBottom: '8px' }}>
            <strong>קורס:</strong> {lecture.courseName}
          </div>
          {lecture.lectureName && (
            <div style={{ marginBottom: '8px' }}>
              <strong>שם מפגש:</strong> {lecture.lectureName}
            </div>
          )}
          <div style={{ marginBottom: '8px' }}>
            <strong>מרצה:</strong> {lecture.lecturer.firstName}{' '}
            {lecture.lecturer.lastName}
          </div>
        </div>

        {/* Time Information */}
        <div
          style={{
            marginBottom: '24px',
            padding: '16px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: '12px' }}>
            זמני ההרצאה:
          </div>
          <div style={{ marginBottom: '8px' }}>
            <strong>זמן התחלה מתוכנן:</strong>{' '}
            {dateUtils.displayFormatDate(lecture.scheduledStartTime)} -{' '}
            {dateUtils.formatTimeFromDate(lecture.scheduledStartTime)}
          </div>
          <div style={{ marginBottom: '8px' }}>
            <strong>זמן סיום מתוכנן:</strong>{' '}
            {dateUtils.displayFormatDate(lecture.scheduledEndTime)} -{' '}
            {dateUtils.formatTimeFromDate(lecture.scheduledEndTime)}
          </div>
          <div style={{ marginBottom: '8px', color: '#2196F3' }}>
            <strong>התחיל בפועל ב:</strong>{' '}
            {dateUtils.displayFormatDate(lecture.sessionStartAt)} -{' '}
            {dateUtils.formatTimeFromDate(lecture.sessionStartAt)}
          </div>
        </div>

        {/* Warning Message */}
        <div
          style={{
            marginBottom: '24px',
            padding: '12px',
            backgroundColor: '#fff3cd',
            borderRight: '4px solid #ffc107',
            borderRadius: '4px',
          }}
        >
          <div style={{ color: '#856404' }}>
            <strong>שים לב:</strong> להרצאה זו לא קיימת שעת סיום. לאחר בחירת
            ועדכון שעת הסיום של ההרצאה, המידע ירשם במערכת והאינדיקציה לסיום ידני
            של ההרצאה תיווצר.
          </div>
        </div>

        {/* End Time Picker */}
        <div style={{ marginBottom: '16px' }}>
          <label
            style={{
              fontWeight: 'bold',
              display: 'block',
              marginBottom: '8px',
            }}
          >
            בחר זמן סיום:
          </label>
          <Calendar
            value={endTime}
            onChange={(e) => {
              setEndTime(e.value as Date);
              setError(null);
            }}
            timeOnly
            hourFormat="24"
            placeholder="בחר זמן סיום"
            style={{ width: '100%', direction: 'ltr' }}
            inputStyle={{ direction: 'rtl', textAlign: 'right' }}
            panelStyle={{ direction: 'ltr' }}
          />
          <div
            style={{
              marginTop: '8px',
              fontSize: '12px',
              color: '#6c757d',
            }}
          >
            זמן הסיום לא יכול להיות יותר מ-5 שעות אחרי זמן הסיום המתוכנן
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div
            style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#f8d7da',
              color: '#721c24',
              borderRadius: '4px',
              borderRight: '4px solid #dc3545',
            }}
          >
            {error}
          </div>
        )}
      </div>
    </Dialog>
  );
};
